/**
 * MiroirDuMatin — V23.15.62. Deux miroirs, deux registres cognitifs.
 *
 *  1. LE MONDE a bougé  — journal mondial/fédéral/cantonal filtré par doctrines.
 *  2. VOTRE MONDE a bougé — journal de l'écosystème personnel.
 *
 * L'utilisateur choisit dans ses préférences quel(s) miroir(s) recevoir, et à
 * quelle échelle géographique pour le miroir monde.
 *
 * Route : /app  (index)
 * Backend :
 *   GET  /api/workspace/mirror/today?scope=auto|world|ecosystem|both
 *   POST /api/workspace/mirror/today/dismiss
 *   GET/POST /api/workspace/mirror/preferences
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Loader2, Sparkles, ArrowRight, RefreshCw, Settings, Globe, Compass, X, Check } from "lucide-react";
import MirrorShareButton from "@/components/MirrorShareButton";
import MirrorInterestsPicker, { saveMirrorPreferences, DEFAULT_MIRROR_PREFS } from "@/components/MirrorInterestsPicker";

const CTA_TO_ROUTE = {
  radar: "/app/radar",
  collision: "/app/ontologie",
  origination: "/app/origination",
  cartographie: "/app/cartographies",
  risques: "/app/risques",
};

const IMPACT_META = {
  eleve: { color: "#E24B4A", label: "élevé" },
  moyen: { color: "#EF9F27", label: "moyen" },
  faible: { color: "#378ADD", label: "faible" },
};

const SCOPE_META = {
  world: {
    label: "Le monde a bougé",
    accent: "#378ADD",
    Icon: Globe,
    overline: "Le monde · aujourd'hui",
  },
  ecosystem: {
    label: "Votre monde a bougé",
    accent: "#EF9F27",
    Icon: Compass,
    overline: "Votre écosystème · aujourd'hui",
  },
};

function fmtDayFrLong() {
  return new Date().toLocaleDateString("fr-FR", {
    weekday: "long", day: "numeric", month: "long",
  });
}

export default function MiroirDuMatin() {
  const [mirrors, setMirrors] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dismissed, setDismissed] = useState({}); // scope -> bool
  const [prefsOpen, setPrefsOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const r = await api.get(`/workspace/mirror/today?scope=auto${force ? "&force=true" : ""}`);
      const list = Array.isArray(r.data?.mirrors) ? r.data.mirrors : (r.data ? [r.data] : []);
      setMirrors(list);
      if (r.data?.preferences) setPreferences(r.data.preferences);
    } catch (e) {
      notify.error(fmtError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const dismiss = async (mirror, relevant) => {
    setDismissed((d) => ({ ...d, [mirror.scope]: true }));
    try {
      await api.post("/workspace/mirror/today/dismiss", { relevant, scope: mirror.scope });
    } catch { /* silent */ }
    if (relevant && mirror?.cta_module && CTA_TO_ROUTE[mirror.cta_module]) {
      navigate(CTA_TO_ROUTE[mirror.cta_module]);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-8 px-4" data-testid="miroir-page">
      <div className="w-full max-w-2xl space-y-6">
        {/* Marqueur temporel — le rituel */}
        <div className="text-center space-y-1 relative">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#EF9F27] font-semibold">News</div>
          <div className="text-[11px] text-zinc-500 first-letter:uppercase" data-testid="miroir-date">{fmtDayFrLong()}</div>
          <button
            type="button"
            onClick={() => setPrefsOpen(true)}
            className="absolute right-0 top-0 text-zinc-500 hover:text-white p-1.5 rounded hover:bg-white/[0.05]"
            data-testid="miroir-prefs-btn"
            title="Préférences des miroirs"
          >
            <Settings className="w-3.5 h-3.5" />
          </button>
        </div>

        {loading ? (
          <div className="text-center text-zinc-500 text-xs flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Noam réfléchit à votre matin…
          </div>
        ) : mirrors.length === 0 ? (
          <div className="text-center text-zinc-500 text-xs" data-testid="miroir-none">
            Aucun miroir activé. <button className="text-[#EF9F27] hover:underline" onClick={() => setPrefsOpen(true)}>Configurer</button>.
          </div>
        ) : (
          <div className="space-y-5">
            {mirrors.map((m) => (
              <MirrorCard
                key={m.scope || "single"}
                mirror={m}
                dismissed={!!dismissed[m.scope]}
                onDismiss={dismiss}
                onNavigate={navigate}
              />
            ))}
          </div>
        )}

        {/* Barre d'actions bas de page */}
        {!loading && mirrors.length > 0 && (
          <div className="flex items-center justify-center gap-4 pt-4 flex-wrap">
            <button
              onClick={() => load(true)}
              disabled={refreshing}
              className="text-[10px] text-zinc-500 hover:text-white inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="miroir-refresh-btn"
            >
              {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Actualiser
            </button>
            <span className="text-zinc-700">·</span>
            <button
              onClick={() => navigate("/app/aujourdhui")}
              className="text-[10px] text-zinc-500 hover:text-white inline-flex items-center gap-1"
              data-testid="miroir-go-cockpit"
            >
              <Sparkles className="w-3 h-3" /> Ouvrir mon environnement complet
            </button>
          </div>
        )}
      </div>

      {prefsOpen && (
        <PreferencesModal
          initial={preferences}
          onClose={() => setPrefsOpen(false)}
          onSaved={async (newPrefs) => {
            setPreferences(newPrefs);
            setPrefsOpen(false);
            await load(false);
          }}
        />
      )}
    </div>
  );
}

function MirrorCard({ mirror, dismissed, onDismiss, onNavigate }) {
  const scope = mirror.scope || "ecosystem";
  const meta = SCOPE_META[scope] || SCOPE_META.ecosystem;
  const impactCfg = IMPACT_META[mirror?.impact_level] || IMPACT_META.moyen;
  const Icon = meta.Icon;

  return (
    <div data-testid={`miroir-card-${scope}`}>
      {/* Overline distincte */}
      <div className="flex items-center gap-2 pb-1.5">
        <Icon className="w-3 h-3" style={{ color: meta.accent }} />
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: meta.accent }}>
          {meta.overline}
        </span>
      </div>

      <div
        className="noam-card p-6 md:p-8 space-y-4 relative"
        style={{ borderColor: `${impactCfg.color}30`, boxShadow: `0 0 24px ${impactCfg.color}12` }}
        data-testid={`miroir-card-inner-${scope}`}
      >
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
          {scope === "world" ? "Ce matin, dans le monde." : "Ce matin, autour de vous."}
        </div>

        <h1
          className="text-xl md:text-2xl font-bold text-white leading-tight"
          data-testid={`miroir-title-${scope}`}
        >
          {mirror.title}
        </h1>

        {mirror.is_meaningful !== false && mirror.body && (
          <p className="text-[13.5px] text-zinc-300 leading-relaxed" data-testid={`miroir-body-${scope}`}>
            {mirror.body}
          </p>
        )}

        {mirror.is_meaningful !== false && (
          <div className="flex items-center gap-3 text-[11px] pt-2 border-t border-white/5 flex-wrap">
            <span className="text-zinc-500">Impact :</span>
            <span className="font-semibold" style={{ color: impactCfg.color }}>{impactCfg.label}</span>
            <span className="text-zinc-600">·</span>
            <span className="text-zinc-500">Fenêtre :</span>
            <span className="text-white font-semibold">{mirror.action_window}</span>
            <span className="text-zinc-600">·</span>
            <span className="text-zinc-500">Confiance :</span>
            <span className="text-zinc-300 tabular-nums">{mirror.confidence}%</span>
          </div>
        )}
      </div>

      {/* Actions par miroir */}
      {!dismissed && mirror.is_meaningful !== false && (
        <div className="flex items-center justify-center gap-3 flex-wrap pt-3" data-testid={`miroir-actions-${scope}`}>
          {mirror.cta_module && CTA_TO_ROUTE[mirror.cta_module] && (
            <button
              onClick={() => onDismiss(mirror, true)}
              className="text-[11px] px-3.5 py-1.5 rounded-full text-black font-semibold hover:brightness-110 inline-flex items-center gap-1.5"
              style={{ background: meta.accent }}
              data-testid={`miroir-cta-btn-${scope}`}
            >
              {mirror.cta_label || "En savoir plus"} <ArrowRight className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={() => onDismiss(mirror, false)}
            className="text-[11px] px-3.5 py-1.5 rounded-full border border-white/15 text-zinc-400 hover:text-white hover:bg-white/[0.03]"
            data-testid={`miroir-dismiss-btn-${scope}`}
          >
            Pas pertinent
          </button>
          <MirrorShareButton mirror={mirror} />
        </div>
      )}
      {dismissed && (
        <div className="text-center text-[10px] text-zinc-500 italic pt-2" data-testid={`miroir-thanks-${scope}`}>
          Merci — Noam en tiendra compte demain matin.
        </div>
      )}
    </div>
  );
}

function PreferencesModal({ initial, onClose, onSaved }) {
  const [prefs, setPrefs] = useState(initial || DEFAULT_MIRROR_PREFS);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!initial) {
      api.get("/workspace/mirror/preferences").then((r) => setPrefs(r.data)).catch(() => {});
    }
  }, [initial]);

  const save = async () => {
    setBusy(true);
    try {
      const saved = await saveMirrorPreferences(prefs, true);
      notify.success("Préférences enregistrées.");
      onSaved(saved);
    } catch (e) {
      notify.error(fmtError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
      data-testid="miroir-prefs-modal"
    >
      <div className="w-full max-w-2xl max-h-[85vh] rounded-lg border border-white/10 bg-[#0A0A0F] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] font-semibold">News</div>
            <div className="text-[15px] text-white font-semibold mt-0.5">Vos préférences</div>
          </div>
          <button onClick={onClose} disabled={busy} className="p-1 hover:bg-white/10 rounded" data-testid="miroir-prefs-close">
            <X className="w-4 h-4 text-zinc-400" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <MirrorInterestsPicker
            value={prefs}
            onChange={setPrefs}
            mode="settings"
          />
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-white/10">
          <button onClick={onClose} disabled={busy} className="btn-secondary text-xs" data-testid="miroir-prefs-cancel">
            Annuler
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="miroir-prefs-save"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
